<template>
    <div class="benchmark-pool-selector">
        <benchmark-pool-fields
            v-if="props.managementContext && !props.disabled"
            ref="poolEditor"
            :model-value="props.modelValue"
            :usage-context="props.managementContext"
            @update:model-value="selectPool"
            @update:validation="editorValidation = $event"
            @pool-config-changed="onPoolSaved"
        />
        <div v-else class="option">
            <label for="savedBenchmarkPool">Benchmark pool</label>
            <select-input input-id="savedBenchmarkPool" :model-value="props.modelValue" :options="poolOptions" option-label="name" option-value="id" :disabled="props.disabled || loading" placeholder="Select a saved pool" @update:model-value="selectPool" />
        </div>
        <div v-if="props.modelValue" class="option">
            <span>Pool view</span>
            <div class="pool-scope-controls" role="group" aria-label="Pool view">
                <primevue-button
                    :outlined="isCategoryScope"
                    :aria-pressed="!isCategoryScope"
                    :disabled="props.disabled || loading"
                    @click="selectCategory('')"
                >
                    Whole pool
                </primevue-button>
                <span class="scope-separator" aria-hidden="true">or</span>
                <select-input
                    class="category-select"
                    input-id="savedBenchmarkPoolCategory"
                    aria-label="Pool category"
                    :model-value="isCategoryScope ? props.scope?.categoryId : null"
                    :options="categoryOptions"
                    option-label="name"
                    option-value="id"
                    placeholder="Select a category"
                    :disabled="props.disabled || loading"
                    @update:model-value="selectCategory"
                />
            </div>
        </div>
        <info-banner v-if="error" severity="error" role="alert" class="pool-error">
            <p>{{ error }}</p>
            <primevue-button class="plain-btn" :disabled="loading" @click="loadPools">Retry</primevue-button>
        </info-banner>
        <info-banner v-else-if="!loading && !pools.length" severity="info" role="status">No saved pools. Create a pool to continue.</info-banner>
    </div>
</template>

<script setup lang="ts">
import {
    computed, onMounted, ref, watch,
} from 'vue';
import InfoBanner from '@/components/common/InfoBanner.vue';
import BenchmarkPoolFields from '@/components/powerMarkets/BenchmarkPoolFields.vue';
import SelectInput from 'primevue/select';
import PrimevueButton from 'primevue/button';
import { benchmarkPoolsApi } from '@/api/powerMarkets/benchmarkPoolsApi.ts';
import { useBenchmarkPoolSessionStore } from '@/stores/powerMarkets/benchmarkPoolSessionStore.ts';
import { BenchmarkPoolScopeTypeEnum, type IBenchmarkPoolRecord, type IBenchmarkPoolScope } from '@/interfaces/powerMarkets/leaderboard/IBenchmarkPool.ts';
import { getBenchmarkPoolErrorMessage } from '@/helpers/powerMarkets/benchmarkPoolErrors.ts';
import { getBenchmarkPoolCategoryDisplayLabel } from '@/helpers/powerMarkets/benchmarkPoolHelpers.ts';

const props = withDefaults(defineProps<{
    modelValue?: string | null;
    scope?: IBenchmarkPoolScope;
    disabled?: boolean;
    managementContext?: 'index' | 'leaderboard';
}>(), {
    modelValue: null, scope: undefined, disabled: false, managementContext: undefined,
});
const emit = defineEmits<{
    'update:modelValue': [string | null];
    'update:scope': [IBenchmarkPoolScope | undefined];
    'update:validation': [string | null];
    change: [];
}>();
const poolEditor = ref<InstanceType<typeof BenchmarkPoolFields> | null>(null);
const editorValidation = ref<string | null>('Please select or create a pool.');

async function requestLeave (): Promise<boolean> {
    return await poolEditor.value?.requestLeave() ?? true;
}
defineExpose({ requestLeave });

const pools = ref<Array<IBenchmarkPoolRecord>>([]);
const loading = ref(true);
const error = ref('');
const selectedPool = computed(() => pools.value.find(pool => pool.id === props.modelValue));
const poolOptions = computed(() => pools.value.map(pool => ({ id: pool.id, name: pool.name })));
const isCategoryScope = computed(() => props.scope?.type === BenchmarkPoolScopeTypeEnum.Category);
const categoryOptions = computed(() => (selectedPool.value?.config.categories ?? [])
    .map(category => ({ id: category.id, name: getBenchmarkPoolCategoryDisplayLabel(category) })));
const validation = computed<string | null>(() => {
    if (props.managementContext && !props.disabled && editorValidation.value) return editorValidation.value;
    if (loading.value) return 'Loading benchmark pools.';
    if (error.value) return error.value;
    if (!selectedPool.value) return 'Please select a saved benchmark pool.';
    if (props.scope?.type === BenchmarkPoolScopeTypeEnum.Category
        && !selectedPool.value.config.categories.some(category => category.id === props.scope?.categoryId)) return 'Please select a category from this pool.';
    return null;
});

async function loadPools (): Promise<void> {
    loading.value = true;
    error.value = '';
    try {
        pools.value = await benchmarkPoolsApi.listBenchmarkPools();
    } catch (failure) {
        error.value = getBenchmarkPoolErrorMessage(failure);
    } finally {
        loading.value = false;
    }
}

function selectPool (id: string | null): void {
    if (props.disabled) return;
    emit('update:modelValue', id);
    if (id !== props.modelValue) emit('update:scope', undefined);
    emit('change');
}

async function onPoolSaved (): Promise<void> {
    await loadPools();
    if (props.scope && !selectedPool.value?.config.categories.some(category => category.id === props.scope?.categoryId)) {
        emit('update:scope', undefined);
    }
    emit('change');
}

function selectCategory (categoryId: string): void {
    if (props.disabled || loading.value) return;
    emit('update:scope', categoryId ? { type: BenchmarkPoolScopeTypeEnum.Category, categoryId } : undefined);
    emit('change');
}

const poolSessionStore = useBenchmarkPoolSessionStore();
watch(() => poolSessionStore.definitionVersions, onPoolSaved);
watch(validation, value => emit('update:validation', value), { immediate: true });
onMounted(loadPools);
</script>

<style scoped lang="scss">
.benchmark-pool-selector {
    display: flex;
    flex-direction: column;
    gap: var(--largeSpacing);
    grid-column: 1 / -1;
}
.option {
    display: flex;
    flex-direction: column;
    gap: var(--smallSpacing);
}
.pool-scope-controls {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: var(--defaultSpacing);
}
.category-select {
    flex: 1 1 12rem;
    min-width: 0;
}
.scope-separator {
    color: var(--neutral600);
}
.pool-error {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: var(--smallSpacing);

    p {
        margin: 0;
    }
}
</style>
